import { Component, computed, effect, signal, WritableSignal } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { RegionDetailDto, RegionMergeDto, RegionService, TeamItemDto, } from '@artemis-software/wr-api';
import { createForm } from '@sonofabit/ng-core';
import { Validators } from '@angular/forms';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { tryWithLoading } from '@/utils/async-utils';
import { PendingChanges } from '@guards/pending-changes.guard';
import { currentTenantId } from '@/utils/admin-utils';
import { AssignmentType } from '@shared/form-controls/generic-pick-list/generic-pick-list.component';

@Component({
  standalone: false,
  selector: 'wr-region-detail-page',
  templateUrl: './region-detail-page.component.html',
  styleUrls: ['./region-detail-page.component.scss']
})
export class RegionDetailPageComponent implements PendingChanges{
  loading$ = new BehaviorSubject(false);
  regionId: string | null = null;
  isEditing = false;

  tenantFilter$ = new BehaviorSubject<string[]>([]);
  readonly currentTenantId = currentTenantId();

  firstSubmit = false;

  region: WritableSignal<RegionDetailDto | undefined> = signal(undefined);

  form?: ReturnType<typeof this.createFormFromDetail>;

  readonly canEdit = signal(false);
  readonly isNew = computed(() => this.regionId && this.regionId === 'new');

  constructor(private readonly router: Router,
    route: ActivatedRoute,
              private readonly dialogService: NbDialogService,
              private readonly regionService: RegionService,
              private readonly nbToastrService: NbToastrService,) {
    route.paramMap.subscribe(async (params) => {
      const id = this.regionId = params.get('id');
      if (id) {
        if (id === 'new') {
          this.isEditing = true;
          this.canEdit.set(true);
          this.form = this.createFormFromDetail(null);
        } else {
          this.regionService.findById(id).subscribe((region) => {
            this.canEdit.set(region.permissions.canEdit);
            this.form = this.createFormFromDetail(region);
            this.form?.disable();
            this.region.set(region);
          });
        }
      } else {
        await this.router.navigate(['regions']);
      }
    });
    effect(() => {
      const tenantId = this.currentTenantId();
      if (tenantId) {
        this.tenantFilter$.next([tenantId]);
      }
    });
  }

  readonly assignmentTypeFunction = (item: TeamItemDto): AssignmentType => {
    if (item.region?.id === this.region()?.id) {
      return AssignmentType.ASSIGNED;
    } else if (item.region?.id !== this.region()?.id && item.region?.id) {
      return AssignmentType.ASSIGNED_ELSEWHERE;
    }
    return AssignmentType.UNASSIGNED;
  };

  async navigateBack(): Promise<void> {
    await this.router.navigate(['regions']);
  }

  onEdit(): void {
    this.isEditing = true;
    this.form?.enable();
  }

  createFormFromDetail(detail: RegionDetailDto | null) {
    return createForm(({ field }) => {
      return {
        id: field({ value: detail?.id }),
        name: field({ value: detail?.name, validators: [Validators.required] }),
        abbreviation: field({ value: detail?.abbreviation, validators: [Validators.required] }),
        description: field({ value: detail?.description }),
        color: field({ value: detail?.color, validators: [Validators.required] }),
        leader: field({ value: detail?.leader, validators: [Validators.required] }),
        teams: field({ value: detail?.teams }),
      };
    });
  }

  async onDelete(): Promise<void> {
    if (await firstValueFrom(this.dialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Bereich löschen',
        message: 'Bist du sicher, dass du den Bereich löschen willst?',
      },
    }).onClose) && this.form?.value.id) {
      await firstValueFrom(this.regionService.deleteById(this.form?.value.id));
      await this.router.navigate(['regions']);
      this.nbToastrService.success('Bereich wurde erfolgreich gelöscht', 'Bereiche');
    }
  }

  async submit(): Promise<void> {
    this.firstSubmit = true;
    this.form?.markAsTouched();

    if (!this.form?.valid) {
      this.nbToastrService.danger('Bereich kann nicht gespeichert werden, es gibt noch Fehler.', 'Bereich');
      return;
    }

    await tryWithLoading(this.loading$, async () => {
      const regionDto = {
        ...this.form?.value,
        leaderId: this.form?.value.leader?.id,
        teamIds: this.form?.value.teams?.map(team => team.id),
      } as RegionMergeDto;
      const saved = await firstValueFrom(this.regionService.merge(regionDto));
      this.form?.get('id')?.setValue(saved.id);
      await this.reloadForm();
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate(['region', saved.id]).then(() => {
          this.nbToastrService.success('Bereich wurde erfolgreich gespeichert', 'Bereich');
        });
      });
    });
  }

  async reloadForm(): Promise<void> {
    await tryWithLoading(this.loading$, async () => {
      const id = this.form?.value.id;
      if (id) {
        const region = await firstValueFrom(this.regionService.findById(id));
        this.form = this.createFormFromDetail(region);
      }
    });
    this.isEditing = false;
    this.form?.disable();
  }

  hasPendingChanges(): boolean | Promise<boolean> {
    return this.isEditing;
  }

  async cancel(): Promise<void> {
    if (!this.isNew()) {
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate(['region', this.regionId]);
      });
    } else {
      await this.router.navigate(['regions']);
    }
  }
}
